import React from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Button, Box, Container, IconButton, Menu, MenuItem,
  Avatar, Divider, useMediaQuery, useTheme, Drawer, List, ListItem, ListItemText,
} from '@mui/material';
import { Menu as MenuIcon, Home, Search, Person, Login } from '@mui/icons-material';
import useAuthStore from '../store/useStore';

function MainLayout() {
  const { isAuthenticated, user, logout } = useAuthStore();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {isMobile && (
              <IconButton onClick={() => setMobileOpen(true)} sx={{ mr: 1 }}>
                <MenuIcon />
              </IconButton>
            )}
            <Typography
              variant="h5" component={Link} to="/"
              sx={{ fontWeight: 700, color: 'primary.main', textDecoration: 'none', flexGrow: isMobile ? 1 : 0, mr: 4 }}
            >
              CampusNest
            </Typography>

            {!isMobile && (
              <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
                <Button component={Link} to="/" color="inherit" sx={{ color: 'text.primary' }}>Home</Button>
                <Button component={Link} to="/properties" color="inherit" sx={{ color: 'text.primary' }}>Find Accommodation</Button>
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isAuthenticated ? (
                <>
                  <Button component={Link} to="/dashboard" variant="outlined" size="small">Dashboard</Button>
                  <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </Avatar>
                  </IconButton>
                  <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                    <MenuItem disabled>
                      <Typography variant="body2" fontWeight={600}>{user?.first_name} {user?.last_name}</Typography>
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={() => { setAnchorEl(null); navigate('/dashboard'); }}>Dashboard</MenuItem>
                    <MenuItem onClick={() => { setAnchorEl(null); navigate('/dashboard/profile'); }}>Profile</MenuItem>
                    <MenuItem onClick={() => { setAnchorEl(null); logout(); navigate('/'); }}>Logout</MenuItem>
                  </Menu>
                </>
              ) : (
                <>
                  <Button component={Link} to="/login" variant="outlined" size="small">Login</Button>
                  <Button component={Link} to="/register" variant="contained" size="small">Sign Up</Button>
                </>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)}>
        <Box sx={{ width: 250, pt: 2 }}>
          <Typography variant="h6" sx={{ px: 2, pb: 2, fontWeight: 700, color: 'primary.main' }}>CampusNest</Typography>
          <Divider />
          <List>
            <ListItem button component={Link} to="/" onClick={() => setMobileOpen(false)}>
              <ListItemText primary="Home" />
            </ListItem>
            <ListItem button component={Link} to="/properties" onClick={() => setMobileOpen(false)}>
              <ListItemText primary="Find Accommodation" />
            </ListItem>
            {isAuthenticated && (
              <ListItem button component={Link} to="/dashboard" onClick={() => setMobileOpen(false)}>
                <ListItemText primary="Dashboard" />
              </ListItem>
            )}
          </List>
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1 }}>
        <Outlet />
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: '#1a1a2e', color: 'white', py: 6, mt: 'auto' }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 4 }}>
            <Box>
              <Typography variant="h6" fontWeight={700} gutterBottom>CampusNest</Typography>
              <Typography variant="body2" color="grey.400">
                The #1 platform for finding student accommodation. On-campus, off-campus - we've got you covered.
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>Quick Links</Typography>
              <Typography variant="body2" color="grey.400" component={Link} to="/properties" sx={{ display: 'block', mb: 1, textDecoration: 'none', color: 'grey.400' }}>Find Accommodation</Typography>
              <Typography variant="body2" color="grey.400" component={Link} to="/register" sx={{ display: 'block', mb: 1, textDecoration: 'none', color: 'grey.400' }}>List Your Property</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>Support</Typography>
              <Typography variant="body2" color="grey.400" sx={{ mb: 1 }}>Help Center</Typography>
              <Typography variant="body2" color="grey.400" sx={{ mb: 1 }}>Safety Info</Typography>
              <Typography variant="body2" color="grey.400" sx={{ mb: 1 }}>Terms of Service</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>Contact</Typography>
              <Typography variant="body2" color="grey.400" sx={{ mb: 1 }}>support@campusnest.com</Typography>
              <Typography variant="body2" color="grey.400" sx={{ mb: 1 }}>+233 30 000 0000</Typography>
            </Box>
          </Box>
          <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.1)' }} />
          <Typography variant="body2" color="grey.500" textAlign="center">
            &copy; {new Date().getFullYear()} CampusNest. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}

export default MainLayout;
